import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getProjectsPage } from '@/api/projects';
import { toHttps } from '@/utils/miscUtils';

const toOption = project => ({
  label: project.label ?? project.name,
  value: project.value ?? project.id,
});

const ProjectPicker = ({ initialNextPage = null, initialProjects = [], onSelect, selectedProjectId }) => {
  const [projects, setProjects] = useState(initialProjects.map(toOption));
  const [nextPage, setNextPage] = useState(toHttps(initialNextPage));
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return projects;

    return projects.filter(project => project.label.toLowerCase().includes(normalizedQuery));
  }, [projects, query]);

  const selectedProject = projects.find(project => project.value === selectedProjectId);

  const loadMore = async () => {
    if (!nextPage || loading) return;

    setLoading(true);
    try {
      const { data } = await getProjectsPage(nextPage);
      const nextProjects = (data?.results ?? []).map(toOption);
      setProjects(currentProjects => [...currentProjects, ...nextProjects]);
      setNextPage(toHttps(data?.next));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-control w-full">
      <div className="label">
        <span className="label-text">Project</span>
      </div>
      <input
        type="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Search visible projects"
        className="input input-bordered input-sm w-full"
      />
      <div className="mt-2 max-h-56 overflow-y-auto rounded border border-gray-200">
        {filteredProjects.map(project => {
          const selected = project.value === selectedProjectId;

          return (
            <button
              key={project.value}
              type="button"
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                selected ? 'bg-gray-100 font-semibold text-gray-900' : 'text-gray-700'
              }`}
              onClick={() => onSelect(project)}
            >
              <span className="truncate">{project.label}</span>
              {selected && <span className="ml-3 text-xs text-gray-500">Selected</span>}
            </button>
          );
        })}
        {!filteredProjects.length && (
          <div className="px-3 py-3 text-sm text-gray-500">
            No visible projects match this search. Load more projects to expand results.
          </div>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="min-w-0 text-xs text-gray-500">
          {selectedProject ? `Selected: ${selectedProject.label}` : 'Select a project'}
        </div>
        {nextPage && (
          <button type="button" className="btn btn-secondary btn-xs" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
};

ProjectPicker.propTypes = {
  initialNextPage: PropTypes.string,
  initialProjects: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func.isRequired,
  selectedProjectId: PropTypes.string,
};

export default ProjectPicker;
